import _ from 'lodash'
import $ from 'cash-dom'
import * as d3 from 'd3-force'
import {fmt, s, greyscale, palette, rewind, ffwd, cancel, offset, sfc32, quadMinMax, bezierMinMax} from './util.js'

export default class Net{
  constructor({book, width, height, seed=1}){
    _.assign(this, {book, width, height, seed})
  }

  static layout({book, seed}){
    const JITTER = 2,
          SETTLE = 500,
          DECAY = 120000

    let loops = [],
        rand = sfc32(seed),
        sim = d3.forceSimulation([])
                .velocityDecay(.175)
                .alphaDecay(1 - Math.pow(0.001, 1 / DECAY) )
                .force("link", d3.forceLink([]).id(d => d.id).distance(30))
                .force("charge", d3.forceManyBody().strength(-4))
                // .force("center", d3.forceCenter())
                // .force("collide", d3.forceCollide().radius(8))
                .force("radial", d3.forceRadial(240).strength(.005))
                .stop()

    let decoratedPg = _.map(book.stats.pages.depths, (d, pg) => ({d, pg:_.parseInt(pg)})),
        pageDepths = _.mapValues(_.groupBy(decoratedPg, 'd'), objs => _.map(objs, 'pg')),
        decoratedChoice = _.map(book.stats.choices.depths, (d, pp) => ({d, pp:_.map(pp.split(','), _.parseInt)})),
        choiceDepths = _.mapValues(_.groupBy(decoratedChoice, 'd'), objs => _.map(objs, 'pp')),
        {endings} = book

    _.times(book.stats.depth+1, depth => {
      let nodes = sim.nodes(),
          links = sim.force('link').links(),
          newNodes = _.map(pageDepths[depth], pg => ({
            id: pg,
            start: depth==0,
            ending: _.includes(endings, pg),
            weight: book.stats.pages.counts[pg] / book.stats.pages.maxCount,
            depth
          })),
          newLinks = _.compact(_.map(choiceDepths[depth], ([source, target]) => {
            let loop = book.stats.pages.depths[target] < depth,
                pair = [source,target].join(','),
                weight = book.stats.choices.counts[pair] / book.stats.choices.maxCount,
                parent = _.find(nodes, {id:source}),
                child = _.find(newNodes, {id:target}) || {};
            child.x = parent.x + 2*JITTER*rand() - JITTER
            child.y = parent.y + 2*JITTER*rand() - JITTER
            return {source, target, loop, weight, depth}
          }))

      sim.nodes(nodes.concat(newNodes))
      sim.force('link').links(links.concat(_.filter(newLinks, {loop:false})))
      loops = loops.concat(_.filter(newLinks, {loop:true}))
      sim.alpha(1)
      sim.tick(SETTLE)
    })

    return {
      seed, loops,
      edges:sim.force('link').links(),
      nodes:sim.nodes()
    }
  }

  attach(dom){
    dom = $(dom).find('.diagram svg')
    let [width, height] = _.map(dom.attr('viewBox').split(' ').slice(2), _.toNumber)
    _.assign(this, {dom, width, height})
    let pageDepth = this.book.stats.pages.depths,
        choiceDepth = this.book.stats.choices.depths,
        choiceCount = this.book.stats.choices.counts,
        choiceMax = this.book.stats.choices.maxCount,
        {endings} = this.book


    let pages = dom.find('.page:not(.target),.ending,.start').map((i,elt) => {
      let id = parseInt(elt.getAttribute('data-page')),
          x = parseFloat(elt.getAttribute('cx')),
          y = parseFloat(elt.getAttribute('cy')),
          ending = _.includes(endings, id),
          depth = pageDepth[id]
      return {id, x, y, ending, depth}
    })

    let nodes = _.zipObject(_.map(pages, 'id'), pages)

    let edges = _.map(choiceDepth, (depth, pair) => {
      let [src, dst] = pair.split(',').map(_.toNumber),
          loop = pageDepth[dst] <= pageDepth[src],
          weight = choiceCount[pair] / choiceMax
      return {source:nodes[src], target:nodes[dst], depth, loop, pair, weight}
    })

    let loops = dom.find('.branch.loop').map((i,elt) => {
      let pair = elt.getAttribute('data-choice'),
          depth = choiceDepth[pair],
          [src, dst] = pair.split(',').map(_.toNumber),
          [x0, y0, cx, cy, x1, y1] = elt.getAttribute('d').match(/M (\S*) (\S*) Q (\S*) (\S*) (\S*) (\S*)/).slice(1).map(_.toNumber)
      return {pair, depth, x0, y0, cx, cy, x1, y1}
    })

    this.layout = {nodes:pages, edges, loops}
    return this
  }

  setDepth(depth){
    let {edges, nodes, loops} = this.layout,
        {choices, endings, depth:maxDepth} = this.book.stats,
        pct = depth % 1,
        inv = 1 - pct,
        rad = 6,
        buds=[], tendrils=[]

    if (pct==0 && depth<maxDepth){
      // draw white dots for all non-ending pages at this depth
      buds = _.filter(nodes, ({depth:d, ending}) => d==depth && !ending)
              .map( ({x,y}) => `<circle fill="white" cx="${fmt(x)}"  cy="${fmt(y)}"  r="${rad}" />`)
    }else if (pct){
      _.filter(edges, b => b.depth!=depth && b.depth==_.ceil(depth)).forEach( arc => {

        if (arc.loop){
          let {x0, y0, cx, cy, x1, y1} = _.find(loops, {pair:arc.pair})
          // from the cabinet of De Casteljau
          let q0 = {x:inv*x0 + pct*cx, y:inv*y0 + pct*cy},
              q1 = {x:inv*cx + pct*x1, y:inv*cy + pct*y1},
              r0 = {x:inv*q0.x + pct*q1.x, y:inv*q0.y + pct*q1.y}

          tendrils.push(`<path class="tendril" fill="none" stroke="green" stroke-dasharray="5,5" d="
              M ${fmt(x0)} ${fmt(y0)}
              Q ${fmt(q0.x)} ${fmt(q0.y)} ${fmt(r0.x)} ${fmt(r0.y)}"/>`)
          buds.push(`<circle fill="white" cx="${fmt(r0.x)}" cy="${fmt(r0.y)}" r="${rad}" />`)
        }else{
          let {source, target, weight} = arc,
              color = palette(1-weight),
              dashes = ''

          let mid = {
            x:inv*source.x + pct*target.x,
            y:inv*source.y + pct*target.y,
          }

          tendrils.push(`<path class="tendril" stroke="${color}" stroke-width="2" ${dashes} d="
              M ${fmt(source.x)} ${fmt(source.y)}
              L ${fmt(mid.x)} ${fmt(mid.y)}"/>`)
          buds.push(`<circle fill="white" cx="${fmt(mid.x)}"  cy="${fmt(mid.y)}"  r="${rad}" />`)
        }
      })
    }

    this.dom.find('.tendrils').html(tendrils.join(' '))
    this.dom.find('.buds').html(buds.join(' '))
  }

  get svg(){
    const DOT = 6

    let {edges, nodes, loops} = Net.layout(this),
        elts = ['<g>']

    // let nodes = this.sim.nodes(),
    //     links = this.sim.force('link').links(),
    //     loops = this.loopbacks;

    let xRange = _.map(nodes, 'x'),
        yRange = _.map(nodes, 'y'),
        top = _.min(yRange) - DOT,
        height = _.max(yRange) - top + DOT,
        centerY = top + height/2,
        left = _.min(xRange) - DOT,
        width = _.max(xRange) - left + DOT,
        centerX = left + width/2,
        dim = _.max([width, height])


    let loopTargets = {}
    _.each(loops, (edge) => {
      let pair = [edge.source, edge.target].join(','),
          source = _.find(nodes, {id:edge.source}),
          target = _.find(nodes, {id:edge.target}),
          theta = Math.atan2(target.y - source.y, target.x - source.x),
          dist = Math.sqrt(Math.pow(target.y - source.y,2) + Math.pow(target.x - source.x,2)),
          step = dist * .45,
          mid = {
            x:(source.x + target.x)/2 + step * Math.cos(theta - Math.PI/2),
            y:(source.y + target.y)/2 + step * Math.sin(theta - Math.PI/2)
          };
      if (loopTargets[edge.target]===undefined){
        let {x,y,start,ending} = target,
            {depth} = edge;
        loopTargets[edge.target] = {x,y,start,ending,depth}
      }
      elts.push(`<path class="loop branch depth-${edge.depth}" data-choice="${pair}" d="M ${source.x} ${source.y} Q ${mid.x} ${mid.y} ${target.x} ${target.y}"/>`)
    })

    _.each(_.values(loopTargets), ({x, y, start, ending, depth}) => {
      let r = 5 + (start || ending ? DOT : DOT*.66)
      elts.push(`<ellipse class="loop target page depth-${depth}" cx="${x}" cy="${y}" rx="${r}" ry="${r}" />`)
    })

    _.each(edges, ({source, target, weight, depth}) => {
      elts.push(`<path class="branch depth-${depth}" stroke="${palette(1 - weight)}" d="M ${source.x} ${source.y} L ${target.x} ${target.y}"/>`)
    })

    elts.push('<g class="tendrils" />')

    _.each(nodes, ({id, x, y, start, ending, weight, depth}) => {
      let tooltip = `<title>pg. ${id}</title>`,
          r = start || ending ? DOT : DOT*.66,
          fill = ending ? 'skyblue'
               : start ? 'black'
               : palette(1 - weight),
          pageClass = ending ? 'ending' : start ? 'start' : 'page',
          ident = start ? 'id="page-one"' : ''
      elts.push(`<ellipse ${ident} class="${pageClass} depth-${depth}" data-page="${id}" data-depth="${depth}" fill="${fill}" cx="${x}" cy="${y}" rx="${r}" ry="${r}">${tooltip}</ellipse>`)
    })


    elts.push('<g class="buds" />')


    // elts.unshift(`<g>`)
    elts.push(`
      </g>
    `)

    return `<svg class="network" xmlns="http://www.w3.org/2000/svg" viewBox="${centerX-dim/2} ${centerY-dim/2} ${dim} ${dim}">${
      elts.join('')
    }</svg>`

  }

}
